import React, { useCallback, useMemo, useRef } from 'react'
import Chart from 'react-apexcharts'
import { Typography, LazyImage, Tooltip } from '@frontend/design_system'
import { DiagramsProps } from './types'
import infoIcon from 'assets/icons/grey400/grey400_infoIcon_16x16.webp'
import { ToggleButtonGroup } from 'ui/molecules/ToggleButtonGroup'
import { diagramVariant, PERIOD, SHORT_PERIOD } from '../../constants'
import { COL_WIDTH, MIN_COL_COUNT, MIN_CONTAINER_WIDTH } from './constants'
import styles from './styles.module.scss'
import { Loader } from 'ui/atoms/Loader'
import { ApexOptions } from 'apexcharts'
import { getDiagramOptions } from '../../utils'

type ChartInstance = { toggleSeries: (seriesName: string) => void }

export const Diagram = React.memo(
  ({
    categories,
    groupCount,
    series,
    isDonut = false,
    onChangePeriod,
    period,
    title,
    titleTooltip,
    currency,
    subtitle,
    variant = diagramVariant.perCar,
    columnsCount = 0,
    isShortPeriod,
    loading,
  }: DiagramsProps) => {
    const chartRef = useRef<HTMLDivElement | null>(null)
    const diagramBlockRef = useRef<HTMLDivElement | null>(null)
    const fixedLegendRef = useRef<HTMLDivElement | null>(null)
    const chartInstanceRef = useRef<ChartInstance | null>(null)

    const chartWidth = useMemo(
      () =>
        columnsCount > MIN_COL_COUNT
          ? columnsCount * COL_WIDTH
          : MIN_CONTAINER_WIDTH,
      [columnsCount]
    )

    const handleLegendPosition = useCallback(() => {
      const legend = chartRef.current?.querySelector(
        '.apexcharts-legend'
      ) as HTMLElement | null

      if (!legend || !diagramBlockRef.current || !chartRef.current) return

      legend.classList.remove('apexcharts-hidden-null-series')
      legend.style.display = 'block'

      if (!fixedLegendRef.current) {
        const fixedLegendContainer = document.createElement('div')
        fixedLegendRef.current = fixedLegendContainer

        Object.assign(fixedLegendContainer.style, {
          position: 'absolute',
          bottom: '56px',
          left: '0px',
          width: '100%',
          background: 'white',
          height: variant === 'schedule' ? '40px' : '85px',
          display: 'flex',
        })

        fixedLegendContainer.addEventListener('click', (event) => {
          const target = event.target as HTMLElement | null
          const seriesItem = target?.closest(
            '.apexcharts-legend-series'
          ) as HTMLElement | null
          const seriesName = seriesItem?.textContent?.trim()
          if (seriesName) {
            chartInstanceRef.current?.toggleSeries(seriesName)
          }
        })

        if (chartRef.current.nextSibling) {
          diagramBlockRef.current.insertBefore(
            fixedLegendContainer,
            chartRef.current.nextSibling
          )
        } else {
          diagramBlockRef.current.appendChild(fixedLegendContainer)
        }
      }

      if (fixedLegendRef.current.childNodes.length > 0) {
        fixedLegendRef.current.innerHTML = ''
      }

      fixedLegendRef.current.appendChild(legend)
      fixedLegendRef.current.style.display = 'flex'

      const remainingLegend = chartRef.current?.querySelector(
        '.apexcharts-legend'
      ) as HTMLElement | null
      if (remainingLegend) {
        remainingLegend.style.display = 'none'
      }
    }, [variant])

    const handleLegendPositionRef = useRef(handleLegendPosition)
    handleLegendPositionRef.current = handleLegendPosition

    const options: ApexOptions = useMemo(() => {
      const baseOptions = getDiagramOptions({
        categoryOptions: categories,
        groupCount,
        isDonut,
        variant,
        period,
        currency,
      })

      const repinLegend = () =>
        requestAnimationFrame(() => handleLegendPositionRef.current())

      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          events: {
            ...baseOptions.chart?.events,
            mounted: (chartContext: ChartInstance) => {
              chartInstanceRef.current = chartContext
              repinLegend()
            },
            updated: repinLegend,
          },
        },
      }
    }, [categories, groupCount, isDonut, variant, period, currency])

    const handlePeriodChange = useCallback(
      (value: string) => {
        onChangePeriod?.(value)
      },
      [onChangePeriod]
    )

    return (
      <div className={styles['diagram-block']} ref={diagramBlockRef}>
        <div className={styles.header}>
          <div>
            <Typography name="h6WSemibold" Tag="div" className={styles.title}>
              {title}
              {titleTooltip && (
                <Tooltip
                  size="primary"
                  popupContent={titleTooltip}
                  placement="bottom"
                >
                  <LazyImage src={infoIcon} width={24} height={24} />
                </Tooltip>
              )}
            </Typography>
            <Typography name="body2WRegular" color="grey500">
              {subtitle}
            </Typography>
          </div>
          {onChangePeriod && (
            <ToggleButtonGroup
              onChange={handlePeriodChange}
              items={isShortPeriod ? SHORT_PERIOD : PERIOD}
              initialValue={period}
            />
          )}
        </div>
        <div className={styles.chart} ref={chartRef}>
          {!loading && series?.length ? (
            <Chart
              options={options}
              series={series}
              type={isDonut ? 'donut' : 'bar'}
              height={370}
              width={chartWidth}
            />
          ) : (
            <div className={styles.loader}>
              <Loader offsetTop={120} />
            </div>
          )}
        </div>
      </div>
    )
  }
)
